/**
 * Factory das chamadas de autenticação. Recebe o transporte (`apiRequest`) do
 * app e devolve as funções de domínio. `TUser` permite ao web manter o seu
 * `User` rico na resposta; o default é o `AuthUser` mínimo (usado no mobile).
 */
export function createAuthApi(request, ctx = {}) {
    const resolveUserAgent = () => {
        try {
            return ctx.userAgent?.() ?? '';
        }
        catch {
            return '';
        }
    };
    const login = (email, password) => request('auth/login', { data: { email, password } }, 'post');
    // Login do aplicativo: aceita todos os tipos no backend (o painel é que
    // bloqueia CLIENT). As restrições do CLIENT são aplicadas na autorização.
    const appLogin = (email, password) => request('auth/app/login', { data: { email, password } }, 'post');
    const refreshAuthToken = (refresh_token) => request('auth/refresh', { data: { refresh_token } }, 'post');
    // Cadastro de cliente no app (cria userType CLIENT na org da plataforma).
    // Não faz auto-login: envia um código OTP por e-mail e retorna
    // { email, requiresVerification }. O login é concluído em verifyClientOtp.
    const registerClient = (data) => request('auth/client/register', { data }, 'post');
    // Verifica o código OTP do cadastro de cliente. Em caso de sucesso, retorna
    // tokens + user (auto-login).
    const verifyClientOtp = (email, code) => request('auth/client/verify-otp', { data: { email, code } }, 'post');
    // Reenvia o código OTP para um cadastro de cliente pendente.
    const resendClientOtp = (email) => request('auth/client/resend-otp', { data: { email } }, 'post');
    const registerPF = (data) => request('auth/register', {
        data: {
            ...data,
            personType: 'FISICA',
            userType: 'COMPANY',
            termsUserAgent: data.termsUserAgent || resolveUserAgent(),
            // Pessoa física: a company É a pessoa. Nome do titular, sem sufixo, e
            // documento é o CPF (enviado em `data.cpf`) — o backend não grava CNPJ
            // quando `personType = FISICA`.
            company: {
                name: data.name,
                tradingName: data.name,
                phone: data.phone,
                segment: data.segment || 'OTHER',
                secondarySegment: data.secondarySegment || 'OTHER',
            },
        },
    }, 'post');
    const registerPJ = (data) => {
        const { companyName, tradingName, ...rest } = data;
        return request('auth/register', {
            data: {
                ...rest,
                personType: 'JURIDICA',
                userType: 'COMPANY',
                termsUserAgent: data.termsUserAgent || resolveUserAgent(),
                company: {
                    name: companyName,
                    tradingName,
                    cnpj: data.cnpj,
                    phone: data.phone,
                    segment: data.segment || 'OTHER',
                    secondarySegment: data.secondarySegment || 'OTHER',
                },
            },
        }, 'post');
    };
    const registerMediator = (data) => {
        const formData = new FormData();
        formData.append('email', data.email);
        if (data.password)
            formData.append('password', data.password);
        formData.append('name', data.name);
        formData.append('phone', data.phone);
        if (data.cpf)
            formData.append('cpf', data.cpf);
        if (data.cnpj)
            formData.append('cnpj', data.cnpj);
        if (data.formation)
            formData.append('formation', data.formation);
        formData.append('personType', data.cnpj ? 'JURIDICA' : 'FISICA');
        formData.append('userType', 'MEDIATOR');
        if (data.files.length > 0) {
            data.files.forEach((file) => {
                formData.append('files', file);
            });
        }
        return request('auth/register', { data: formData, formData: true }, 'post');
    };
    const forgotPassword = (email) => request('auth/forgot-password', { data: { email } }, 'post');
    const resetPassword = (token, password) => request('auth/reset-password', { data: { token, password } }, 'post');
    const resendVerificationEmail = (email) => request('auth/resend-verification', { data: { email } }, 'post');
    /**
     * Resolve o token do link de cadastro (uso único) e retorna o plano para a
     * página de registro exibir. Não consome o token — o consumo/rotação ocorre
     * ao concluir o cadastro.
     */
    const resolveRegistrationLink = (token) => request(`auth/registration-link/${encodeURIComponent(token)}`, {}, 'get');
    const validatePasswordSetup = (token) => request(`auth/validate-password-setup?token=${token}`, {}, 'get');
    const setupPassword = (token, password) => request('auth/setup-password', { data: { token, password } }, 'post');
    return {
        login,
        appLogin,
        registerClient,
        verifyClientOtp,
        resendClientOtp,
        refreshAuthToken,
        registerPF,
        registerPJ,
        registerMediator,
        forgotPassword,
        resetPassword,
        resendVerificationEmail,
        resolveRegistrationLink,
        validatePasswordSetup,
        setupPassword,
    };
}
//# sourceMappingURL=auth-api.js.map